import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Check, X, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { authService } from '../services/api';
import { useSEO } from '../hooks/useSEO';

const ResetPassword = () => {
  useSEO({
    title: 'Reset Password',
    description: 'Set a new password for your Family Wishlist account.',
    keywords: 'reset password, family wishlist, account recovery',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      'name': 'Reset Password - Family Wishlist',
      'description': 'Set a new password for your Family Wishlist account.',
      'url': `${window.location.origin}/reset-password`
    }
  });

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordMatchError, setPasswordMatchError] = useState(false);

  const requirements = [
    { label: 'At least 8 characters', test: (p) => p.length >= 8 },
    { label: 'At least one uppercase letter', test: (p) => /[A-Z]/.test(p) },
    { label: 'At least one lowercase letter', test: (p) => /[a-z]/.test(p) },
    { label: 'At least one number', test: (p) => /\d/.test(p) },
    { label: 'At least one special character', test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p) },
  ];

  useEffect(() => {
    if (confirmNewPassword && newPassword !== confirmNewPassword) {
      setPasswordMatchError(true);
    } else {
      setPasswordMatchError(false);
    }
  }, [newPassword, confirmNewPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Invalid or missing password reset token.');
      return;
    }

    const allMet = requirements.every(req => req.test(newPassword));
    if (!allMet) {
      setError('Please ensure your password meets all requirements.');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      await authService.resetPassword({
        token,
        newPassword,
        confirmNewPassword
      });
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="max-w-md mx-auto mt-20 card text-center space-y-4">
        <AlertCircle className="mx-auto text-xmas-red" size={48} />
        <h2 className="text-2xl font-bold text-gray-800">Invalid Reset Link</h2>
        <p className="text-sm text-gray-600">
          This password reset link is invalid or incomplete. Please request a new link.
        </p>
        <Link to="/forgot-password" className="btn-primary inline-block">
          Request New Link
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto mt-20 card">
      <h2 className="text-3xl font-bold text-center text-xmas-red mb-2">Reset Password</h2>
      <p className="text-sm text-gray-600 text-center mb-6">
        Enter your new password below.
      </p>

      {success ? (
        <div className="bg-green-50 border border-green-200 text-green-800 p-4 rounded-md text-center space-y-3">
          <CheckCircle2 className="mx-auto text-xmas-green" size={40} />
          <p className="text-base font-medium">Password Reset Successful!</p>
          <p className="text-sm text-gray-600">Your password has been reset. Redirecting you to sign in...</p>
          <Link to="/login" className="btn-primary inline-block text-sm mt-2">
            Sign In Now
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">New Password</label>
            <div className="relative mt-1">
              <input
                type={showPassword ? 'text' : 'password'}
                className="w-full p-2 border rounded pr-10 focus:ring-xmas-green focus:border-xmas-green"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="absolute right-2 top-2 text-gray-500"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            <div className="mt-2 space-y-1">
              {requirements.map((req, index) => {
                const met = req.test(newPassword);
                return (
                  <div key={index} className={`flex items-center text-xs ${met ? 'text-green-600' : 'text-gray-500'}`}>
                    {met ? <Check size={12} className="mr-1" /> : <X size={12} className="mr-1" />}
                    {req.label}
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Confirm New Password</label>
            <div className="relative mt-1">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                className={`w-full p-2 border rounded pr-10 focus:ring-xmas-green focus:border-xmas-green ${passwordMatchError ? 'border-red-500' : ''}`}
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="absolute right-2 top-2 text-gray-500"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {passwordMatchError && (
              <p className="text-red-500 text-xs mt-1">Passwords do not match.</p>
            )}
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="btn-primary w-full flex justify-center items-center gap-2 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                Resetting Password...
              </>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>
      )}
    </div>
  );
};

export default ResetPassword;
